import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Hammer, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ScrollReveal } from "@/hooks/useScrollReveal";
import { MicroSplash } from "@/components/MicroSplash";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_truth-centered/artifacts/4bkl7m0w_B2F741E8-3ED3-4C5F-8567-3D82A405954C.png";

const pathMessages = {
  seller: {
    title: "Welcome, Steward of the Land",
    scripture: "The earth is the Lord's, and the fulness thereof.",
    reference: "Psalm 24:1",
    message: "You've been entrusted with a portion of God's creation. Whether you're ready to sell or simply exploring options, we're here to serve you with honesty, clarity, and respect for your dignity.",
    cta: "Continue as Land Seller",
    link: "/seller"
  },
  builder: {
    title: "Welcome, Builder of Communities",
    scripture: "Except the Lord build the house, they labour in vain that build it.",
    reference: "Psalm 127:1",
    message: "You transform land into homes and communities. We partner with builders who value integrity, seeking to provide clean deals with full disclosure and fair economics.",
    cta: "Continue as Builder",
    link: "/builder"
  }
};

const covenantPrinciples = [
  "Full transparency with all parties",
  "Clear disclosure of intent",
  "Fair pricing grounded in real math",
  "Freedom to walk away from any deal"
];

export default function EntryGate() {
  const [selectedPath, setSelectedPath] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [visiblePrinciple, setVisiblePrinciple] = useState(0);
  const [splashComplete, setSplashComplete] = useState(false);
  const messageRef = useRef(null);

  // Handle path selection with smooth scroll
  const handlePathSelect = (path) => {
    if (selectedPath === path) {
      setSelectedPath(null);
    } else {
      setSelectedPath(path);
      // Scroll to message after a brief delay for animation
      setTimeout(() => {
        messageRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 150);
    }
  };

  // Progressive principle reveal
  useEffect(() => {
    if (selectedPath) {
      setCurrentStep(2);
      const interval = setInterval(() => {
        setVisiblePrinciple(prev => {
          if (prev < covenantPrinciples.length - 1) return prev + 1;
          clearInterval(interval);
          return prev;
        });
      }, 600);
      return () => clearInterval(interval);
    } else {
      setCurrentStep(1);
      setVisiblePrinciple(0);
    }
  }, [selectedPath]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="entry-gate">
      <MicroSplash onComplete={() => setSplashComplete(true)} />
      <SEO 
        title="Ethical Land Facilitation & Stewardship"
        description="Steward Lots facilitates transparent land transactions connecting landowners and builders through ethical, fully disclosed deal structuring."
        path="/"
      />
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12 md:py-16">
        {/* Logo */}
        <div className="gentle-reveal mb-6">
          <img 
            src={LOGO_URL} 
            alt="Steward Lots logo - Christ-led land stewardship connecting land sellers with builders through ethical, Scripture-based facilitation" 
            className="w-56 md:w-72 h-auto"
            data-testid="logo"
          />
        </div>

        {/* H1 - Primary Page Heading */}
        <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1C1917] text-center max-w-2xl mb-3 gentle-reveal" style={{ animationDelay: '0.2s' }}>
          Christ-Led Land Stewardship
        </h1>

        {/* Tagline */}
        <p className="text-base md:text-lg text-[#57534E] text-center max-w-xl mb-6 gentle-reveal" style={{ animationDelay: '0.4s' }}>
          Ethical land facilitation connecting landowners and builders through transparent, fully disclosed transactions.
        </p>

        {/* Live Indicator */}
        <div className="live-indicator text-sm text-[#A8A29E] mb-8 gentle-reveal" style={{ animationDelay: '0.5s' }}>
          <span className="live-dot" aria-hidden="true"></span>
          <span>Active stewardship in progress</span>
        </div>

        {/* Step Progress */}
        <div className="w-full max-w-md mb-8 gentle-reveal" style={{ animationDelay: '0.6s' }}>
          <div className="step-indicator">
            <div className={`step-number ${currentStep >= 1 ? 'active' : 'pending'}`}>1</div>
            <div className="step-line">
              <div className="step-line-fill" style={{ width: currentStep >= 2 ? '100%' : '0%' }}></div>
            </div>
            <div className={`step-number ${currentStep >= 2 ? 'active' : 'pending'}`}>2</div>
            <div className="step-line">
              <div className="step-line-fill" style={{ width: currentStep >= 3 ? '100%' : '0%' }}></div>
            </div>
            <div className={`step-number ${currentStep >= 3 ? 'active' : 'pending'}`}>3</div>
          </div>
          <div className="flex justify-between text-xs text-[#A8A29E] mt-2 px-1">
            <span>Discern</span>
            <span>Understand</span>
            <span>Begin</span>
          </div>
        </div>

        {/* Guided Experience */}
        <div className="w-full max-w-2xl">
          {/* Step 1: Path Selection */}
          <div className={`transition-all duration-500 ${selectedPath ? 'opacity-60' : 'opacity-100'}`}>
            <p className="text-center text-[#57534E] text-sm mb-4">
              How may we serve you?
            </p>

            {/* Path Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* Seller Path */}
              <button
                onClick={() => handlePathSelect("seller")}
                className={`path-card-enhanced group relative p-6 text-left bg-[#F5F5F4] border transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BFA15F] ${
                  selectedPath === "seller" 
                    ? "border-[#BFA15F] bg-[#FBF9F3] shadow-lg" 
                    : selectedPath === "builder"
                    ? "border-[#E7E5E4] opacity-50"
                    : "border-[#E7E5E4] hover:border-[#BFA15F]/50"
                }`}
                data-testid="path-seller"
                aria-pressed={selectedPath === "seller"}
              >
                <div className="flex items-start gap-4">
                  <div className={`path-icon-container w-12 h-12 flex items-center justify-center transition-all duration-300 ${
                    selectedPath === "seller" ? "bg-[#BFA15F] text-white" : "bg-white text-[#57534E]"
                  }`}>
                    <Home className="path-icon-inner w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#1C1917] mb-1">I Own Land</h3>
                    <p className="text-sm text-[#57534E]">Exploring options to sell</p>
                  </div>
                </div>
                {selectedPath === "seller" && (
                  <div className="absolute top-4 right-4">
                    <ChevronRight className="w-5 h-5 text-[#BFA15F]" />
                  </div>
                )}
              </button>

              {/* Builder Path */}
              <button
                onClick={() => handlePathSelect("builder")}
                className={`path-card-enhanced group relative p-6 text-left bg-[#F5F5F4] border transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BFA15F] ${
                  selectedPath === "builder" 
                    ? "border-[#BFA15F] bg-[#FBF9F3] shadow-lg" 
                    : selectedPath === "seller"
                    ? "border-[#E7E5E4] opacity-50"
                    : "border-[#E7E5E4] hover:border-[#BFA15F]/50"
                }`}
                data-testid="path-builder"
                aria-pressed={selectedPath === "builder"}
              >
                <div className="flex items-start gap-4">
                  <div className={`path-icon-container w-12 h-12 flex items-center justify-center transition-all duration-300 ${
                    selectedPath === "builder" ? "bg-[#BFA15F] text-white" : "bg-white text-[#57534E]"
                  }`}>
                    <Hammer className="path-icon-inner w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#1C1917] mb-1">I Build or Buy Land</h3>
                    <p className="text-sm text-[#57534E]">Seeking opportunities</p>
                  </div>
                </div>
                {selectedPath === "builder" && (
                  <div className="absolute top-4 right-4">
                    <ChevronRight className="w-5 h-5 text-[#BFA15F]" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Step 2: Scripture Message & Principles */}
          {selectedPath && (
            <div ref={messageRef} className="gentle-reveal">
              <div className="bg-white border border-[#E7E5E4] p-6 md:p-8 mb-6">
                {/* Scripture */}
                <div className="border-l-[3px] border-[#BFA15F] pl-4 mb-6">
                  <p className="font-serif italic text-[#57534E] text-lg">
                    "{pathMessages[selectedPath].scripture}"
                  </p>
                  <p className="text-sm text-[#A8A29E] mt-2 uppercase tracking-wide">
                    {pathMessages[selectedPath].reference}
                  </p>
                </div>
                
                {/* Welcome */}
                <h2 className="font-serif text-xl md:text-2xl text-[#1C1917] mb-3">
                  {pathMessages[selectedPath].title}
                </h2>
                <p className="text-[#57534E] mb-6">
                  {pathMessages[selectedPath].message}
                </p>

                {/* Progressive Covenant Principles */}
                <div className="border-t border-[#E7E5E4] pt-6 mb-6">
                  <p className="text-xs uppercase tracking-wider text-[#A8A29E] mb-4">Our Covenant to You</p>
                  <ul className="space-y-3">
                    {covenantPrinciples.map((principle, index) => (
                      <li 
                        key={index}
                        className={`flex items-center gap-3 text-sm transition-all duration-500 ${
                          index <= visiblePrinciple ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 bg-[#BFA15F] rounded-full flex-shrink-0"></span>
                        <span className="text-[#57534E]">{principle}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <Link
                  to={pathMessages[selectedPath].link}
                  className="btn-enhanced inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0F0F10] text-white font-medium transition-all w-full sm:w-auto"
                  data-testid={`cta-${selectedPath}`}
                >
                  {pathMessages[selectedPath].cta}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Faith Statement */}
        <div className="text-center max-w-lg mt-8 gentle-reveal" style={{ animationDelay: '0.8s' }}>
          <div className="inline-block border-l-[3px] border-[#BFA15F] pl-4 text-left">
            <p className="font-serif italic text-[#57534E] mb-2">
              "Jesus Christ is Lord."
            </p>
            <p className="text-sm text-[#A8A29E]">
              We conduct business by God's commands, not convenience.
            </p>
          </div>
        </div>
      </main>

      {/* Covenant Promise Section */}
      <ScrollReveal animation="reveal-up" className="bg-[#F5F5F4] py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-4">
            Our Covenant Accountability Model
          </h2>
          <p className="text-[#57534E] mb-6">
            Every interaction with Steward Lots™ is governed by our commitment to truth, transparency, and respect for your dignity as an image-bearer of God.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="bg-white p-4 border border-[#E7E5E4] interactive-card">
              <p className="font-medium text-[#1C1917] mb-1">Full Disclosure</p>
              <p className="text-[#57534E]">We tell you everything we know</p>
            </div>
            <div className="bg-white p-4 border border-[#E7E5E4] interactive-card">
              <p className="font-medium text-[#1C1917] mb-1">Honest Compensation</p>
              <p className="text-[#57534E]">Our fees are disclosed in writing</p>
            </div>
            <div className="bg-white p-4 border border-[#E7E5E4] interactive-card">
              <p className="font-medium text-[#1C1917] mb-1">No Pressure</p>
              <p className="text-[#57534E]">We respect your timeline</p>
            </div>
          </div>
        </div>
      </ScrollReveal>

      {/* What is Land Facilitation Section */}
      <ScrollReveal animation="reveal-up" className="bg-white py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
            What is Land Facilitation?
          </h2>
          <div className="text-[#57534E] space-y-4">
            <p>
              Land facilitation is the process of connecting land sellers with qualified buyers—typically builders, developers, or investors—through ethical and transparent deal structuring. Unlike traditional real estate brokerage, land facilitation focuses on creating direct, efficient transactions where all parties understand the full economics of the deal.
            </p>
            <p>
              At Steward Lots, we practice land stewardship with integrity. We believe that transparent land deals benefit everyone involved. When you work with us, you'll know exactly what we're paying for your property, what we intend to sell it for, and who the end buyer is. There are no hidden fees, no bait-and-switch tactics, and no pressure to make quick decisions.
            </p>
          </div>
        </div>
      </ScrollReveal>

      {/* Services for Landowners Section */}
      <ScrollReveal animation="reveal-up" className="bg-[#F5F5F4] py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
            For Land Sellers
          </h2>
          <div className="text-[#57534E] space-y-4">
            <p>
              If you own land and are considering selling, Steward Lots offers a straightforward path forward. We work with landowners who want to sell their property without the hassle of traditional listings, showings, and uncertain timelines. Our land facilitation process is designed to respect your time and provide clarity at every step.
            </p>
            <p>
              We provide fair, honest offers based on real market data—not lowball tactics. You'll receive a written offer that clearly explains our valuation methodology, and you're free to accept, decline, or negotiate without any pressure. We believe ethical land transactions start with treating sellers as partners, not targets.
            </p>
          </div>
        </div>
      </ScrollReveal>

      {/* Services for Builders Section */}
      <ScrollReveal animation="reveal-up" className="bg-white py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
            For Builders & Land Buyers
          </h2>
          <div className="text-[#57534E] space-y-4">
            <p>
              Builders and developers need reliable access to quality land opportunities. Steward Lots connects you with properties that match your acquisition criteria—lot size, location, zoning, and price point. Every deal we present comes with full disclosure of our acquisition cost and fee structure.
            </p>
            <p>
              We believe transparent land deals create better business relationships. When you know exactly what you're paying and why, you can make informed decisions that serve your building projects. Our goal is to be a trusted partner in your land acquisition process, providing clean deals with clear title and honest economics.
            </p>
          </div>
        </div>
      </ScrollReveal>

      {/* How It Works Section */}
      <ScrollReveal animation="reveal-up" className="bg-[#F5F5F4] py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
            How Our Facilitation Process Works
          </h2>
          <div className="text-[#57534E] space-y-4">
            <p>
              Our land facilitation process is simple and transparent. For <Link to="/seller" className="text-[#BFA15F] hover:underline">sellers</Link>, it begins with a conversation about your property and goals. We'll research comparable sales, assess the property's potential, and present you with a fair offer—typically within 24-48 hours. If you accept, we handle the paperwork and coordinate with title companies to ensure a smooth closing.
            </p>
            <p>
              For <Link to="/builder" className="text-[#BFA15F] hover:underline">builders and land buyers</Link>, we maintain an active inventory of land opportunities across our service areas in Ohio and beyond. When we acquire a property that matches your <Link to="/builder/buybox" className="text-[#BFA15F] hover:underline">buy box criteria</Link>, we'll reach out with complete details including our acquisition cost, intended sale price, and all known property information. You'll have time to conduct due diligence before making any commitments.
            </p>
          </div>
        </div>
      </ScrollReveal>

      {/* FAQ Section */}
      <ScrollReveal animation="reveal-up" className="bg-white py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
            Common Questions About Selling Land
          </h2>
          <div className="text-[#57534E] space-y-6">
            <div>
              <h3 className="font-medium text-[#1C1917] mb-2">How is land facilitation different from listing with an agent?</h3>
              <p>Traditional listings can take months and involve showings, negotiations, and uncertainty. Land facilitation provides a direct sale to a ready buyer with a clear timeline and no commissions or listing fees deducted from your proceeds. Learn more about our <Link to="/faith" className="text-[#BFA15F] hover:underline">faith-based approach</Link> to ethical transactions.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#1C1917] mb-2">Will I get a fair price for my land?</h3>
              <p>We base our offers on current market data and comparable sales in your area. While we need to build in margin for our facilitation services, we're committed to making offers that are fair and clearly explained. You'll always know how we arrived at our number.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#1C1917] mb-2">How long does the process take?</h3>
              <p>Most transactions close within 2-4 weeks, though we can accommodate faster or slower timelines based on your needs. We'll work with your schedule and never rush you into a decision.</p>
            </div>
            <div>
              <h3 className="font-medium text-[#1C1917] mb-2">Are there any fees or obligations?</h3>
              <p>There are no fees to receive an offer, and you're under no obligation to accept. If you decide to move forward, we typically cover standard closing costs. Everything is disclosed upfront—no surprises at the closing table. Have questions? <Link to="/contact" className="text-[#BFA15F] hover:underline">Contact us</Link> anytime.</p>
            </div>
          </div>
        </div>
      </ScrollReveal>

      {/* Footer Links */}
      <footer className="py-6 px-6 border-t border-[#E7E5E4] bg-white">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-6 text-sm text-[#A8A29E]">
          <Link to="/faith" className="hover:text-[#1C1917] transition-colors" data-testid="nav-faith">
            Faith & Principles
          </Link>
          <Link to="/about" className="hover:text-[#1C1917] transition-colors" data-testid="nav-about">
            About Us
          </Link>
          <Link to="/prayer" className="hover:text-[#1C1917] transition-colors" data-testid="nav-prayer">
            Prayer Requests
          </Link>
          <Link to="/contact" className="hover:text-[#1C1917] transition-colors" data-testid="nav-contact">
            Contact Steward Lots
          </Link>
        </div>
      </footer>
    </div>
  );
}
